import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/2f4f4ce3-8121-45e6-bf04-63921ac249c0.png';

const navLinks = [
  { label: 'Inicio', to: '/' },
  { label: 'Nosotros', to: '/nosotros' },
  { label: 'Servicios', to: '/servicios' },
  { label: 'Opiniones', to: '/opiniones' },
  { label: 'Sucursales', to: '/sucursales' },
  { label: 'Galería', to: '/galeria' },
  { label: 'Contacto', to: '/contacto' },
];

interface NavbarProps {
  onOpenModal: () => void;
}

export default function Navbar({ onOpenModal }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-[#0A2540] shadow-[0_4px_30px_rgba(10,37,64,0.4)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center">
            <img src={LOGO_URL} alt="MF Dental" className="h-10 w-auto object-contain" />
          </div>
          <div className="hidden sm:block leading-tight">
            <span className="block text-white font-bold text-lg tracking-wide">MF Dental</span>
            <span className="block text-[#3A86FF] text-xs font-medium">Rehabilitación Dental</span>
          </div>
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer relative group ${
                isActive(l.to) ? 'text-[#3A86FF]' : 'text-white/75 hover:text-[#3A86FF]'
              }`}
            >
              {l.label}
              <span
                className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#3A86FF] transition-all duration-200 ${
                  isActive(l.to) ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:5534748982"
            className="flex items-center gap-2 text-white/70 hover:text-[#3A86FF] text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm" />
            </div>
            55 3474 8982
          </a>
          <button
            onClick={onOpenModal}
            className="bg-[#3A86FF] hover:bg-[#1a6aff] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(58,134,255,0.35)]"
          >
            Agendar Cita
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#0A2540]/98 backdrop-blur-md border-t border-white/10 px-6 py-5">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className={`flex w-full text-left py-3 text-sm font-medium border-b border-white/5 cursor-pointer transition-colors whitespace-nowrap ${
                isActive(l.to) ? 'text-[#3A86FF]' : 'text-white/80 hover:text-[#3A86FF]'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => { onOpenModal(); setMenuOpen(false); }}
            className="mt-5 w-full bg-[#3A86FF] text-white py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer"
          >
            Agendar Cita
          </button>
        </div>
      )}
    </nav>
  );
}
